/**
 * Admin Tenant Hooks
 *
 * React Query hooks for superadmin tenant management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { parseApiError, getErrorMessage } from '@/lib/api-errors';
import { toast } from 'sonner';
import { impersonation, decodeJwt } from '@/lib/api-client';
import { useBoundStore } from '@/stores';
import { tenantsApi, operationsApi } from '../services/admin.service';
import type { TenantListFilters, CreateTenantData, UpdateTenantData, TenantAction } from '../types';
import type { UserRole } from '@/features/auth/types/auth.types';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const tenantUserKeys = {
  all: ['admin', 'tenants'] as const,
  lists: () => [...tenantUserKeys.all, 'list'] as const,
  list: (filters?: TenantListFilters) => [...tenantUserKeys.lists(), filters] as const,
  stats: () => [...tenantUserKeys.all, 'stats'] as const,
  statusDistribution: () => [...tenantUserKeys.all, 'status-distribution'] as const,
  detail: (id: string) => [...tenantUserKeys.all, 'detail', id] as const,
  lifecycle: (id: string) => [...tenantUserKeys.detail(id), 'lifecycle'] as const,
  lifecycleSummary: (id: string) => [...tenantUserKeys.lifecycle(id), 'summary'] as const,
  lifecycleHistory: (id: string, filters?: { limit?: number; offset?: number }) =>
    [...tenantUserKeys.lifecycle(id), 'history', filters] as const,
  suspendedLists: () => [...tenantUserKeys.all, 'suspended'] as const,
  suspended: (filters?: { limit?: number; offset?: number }) =>
    [...tenantUserKeys.suspendedLists(), filters] as const,
};

// =============================================================================
// QUERIES
// =============================================================================

export function useTenantsList(filters?: TenantListFilters) {
  return useQuery({
    queryKey: tenantUserKeys.list(filters),
    queryFn: () => tenantsApi.list(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useTenantStats() {
  return useQuery({
    queryKey: tenantUserKeys.stats(),
    queryFn: tenantsApi.getStats,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useTenantStatusDistribution() {
  return useQuery({
    queryKey: tenantUserKeys.statusDistribution(),
    queryFn: tenantsApi.getStatusDistribution,
    staleTime: 1000 * 60 * 5,
  });
}

export function useTenantDetail(id: string) {
  return useQuery({
    queryKey: tenantUserKeys.detail(id),
    queryFn: () => tenantsApi.getById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  });
}

export function useTenantLifecycleSummary(id: string) {
  return useQuery({
    queryKey: tenantUserKeys.lifecycleSummary(id),
    queryFn: () => tenantsApi.getLifecycleSummary(id),
    enabled: !!id,
    staleTime: 1000 * 60,
  });
}

export function useTenantLifecycleHistory(
  id: string,
  filters: { limit?: number; offset?: number } = { limit: 25, offset: 0 },
) {
  return useQuery({
    queryKey: tenantUserKeys.lifecycleHistory(id, filters),
    queryFn: () => tenantsApi.getLifecycleHistory(id, filters),
    enabled: !!id,
    staleTime: 1000 * 60,
  });
}

export function useSuspendedTenants(filters?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: tenantUserKeys.suspended(filters),
    queryFn: () => tenantsApi.getSuspendedTenants(filters),
    staleTime: 1000 * 60 * 2,
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

export function useCreateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTenantData) => tenantsApi.create(data),

    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.stats() });
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.statusDistribution() });
      toast.success('Tenant Created', {
        description: `${tenant.name} has been created successfully`,
      });
    },

    onError: () => {
      toast.error('Failed to create tenant', { description: 'Please try again' });
    },
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTenantData }) =>
      tenantsApi.update(id, data),

    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.detail(tenant.id) });
      toast.success('Tenant Updated', {
        description: 'Changes have been saved',
      });
    },

    onError: () => {
      toast.error('Failed to update tenant', { description: 'Please try again' });
    },
  });
}

export function useDeleteTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tenantsApi.delete(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.stats() });
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.statusDistribution() });
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.suspendedLists() });
      toast.success('Tenant deactivated', {
        description: 'Tenant data is preserved for audit and recovery',
      });
    },

    onError: () => {
      toast.error('Failed to deactivate tenant', { description: 'Please try again' });
    },
  });
}

export function useTenantAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: TenantAction }) =>
      tenantsApi.performAction(id, action),

    onSuccess: (tenant, { action }) => {
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.detail(tenant.id) });
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.stats() });
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.statusDistribution() });
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.lifecycle(tenant.id) });
      queryClient.invalidateQueries({ queryKey: tenantUserKeys.suspendedLists() });

      const actionMessages: Record<TenantAction['type'], string> = {
        suspend: `${tenant.name} has been suspended`,
        activate: `${tenant.name} has been activated`,
        cancel: `${tenant.name} subscription has been cancelled`,
        extend_trial: `Trial extended for ${tenant.name}`,
        upgrade: `${tenant.name} has been upgraded`,
        downgrade: `${tenant.name} has been downgraded`,
      };

      toast.success('Action Completed', {
        description: actionMessages[action.type],
      });
    },

    onError: () => {
      toast.error('Action failed', { description: 'Please try again' });
    },
  });
}

// Helper to map JWT role to UserRole
function mapJwtRoleToUserRole(role: string): UserRole {
  const roleMap: Record<string, UserRole> = {
    superadmin: 'SUPER_ADMIN',
    super_admin: 'SUPER_ADMIN',
    tenant_admin: 'TENANT_ADMIN',
    tenantadmin: 'TENANT_ADMIN',
    crm_admin: 'CRM_ADMIN',
    crmadmin: 'CRM_ADMIN',
    hr_manager: 'HR_MANAGER',
    hrmanager: 'HR_MANAGER',
    manager: 'MANAGER',
    employee: 'EMPLOYEE',
  };
  return roleMap[role.toLowerCase()] || 'EMPLOYEE';
}

// Select the highest priority role from the roles array
function getHighestRole(roles: string[]): UserRole {
  const rolePriority: UserRole[] = [
    'SUPER_ADMIN',
    'TENANT_ADMIN',
    'CRM_ADMIN',
    'HR_MANAGER',
    'MANAGER',
    'EMPLOYEE',
  ];
  const mappedRoles = roles.map(mapJwtRoleToUserRole);
  for (const priorityRole of rolePriority) {
    if (mappedRoles.includes(priorityRole)) {
      return priorityRole;
    }
  }
  return 'EMPLOYEE';
}

export function useImpersonateTenant() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (id: string) => tenantsApi.impersonate(id),

    onSuccess: (data) => {
      // Store impersonation token (also sets 'impersonating' flag in sessionStorage)
      impersonation.setImpersonationToken(data.accessToken);

      // Decode the token to get user info
      const jwtPayload = decodeJwt(data.accessToken);

      // Create enriched user from JWT and backend response
      const impersonatedUser = {
        id: data.user.id,
        email: data.user.email,
        firstName: data.user.firstName,
        lastName: data.user.lastName,
        fullName: `${data.user.firstName} ${data.user.lastName}`,
        role: jwtPayload?.roles?.length
          ? getHighestRole(jwtPayload.roles)
          : mapJwtRoleToUserRole(data.user.role || 'tenant_admin'),
        permissions: data.user.permissions || jwtPayload?.permissions || [],
        tenantId: data.user.tenantId || jwtPayload?.tenantId,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Update the store with impersonated user
      const { login: storeLogin } = useBoundStore.getState();
      storeLogin(impersonatedUser, {
        id: data.user.tenantId || jwtPayload?.tenantId || '',
        name: jwtPayload?.tenantSlug || '',
        code: jwtPayload?.tenantSlug || '',
      });

      toast.success('Impersonation Started', {
        description: 'You are now viewing as the tenant admin',
      });

      // Navigate to dashboard in the same tab
      navigate({ to: '/dashboard' });
    },

    onError: (error: unknown) => {
      toast.error('Impersonation failed', { description: getErrorMessage(parseApiError(error)) });
    },
  });
}

/**
 * Hook to stop impersonation and return to superadmin view
 */
export function useStopImpersonation() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      // Get original token before clearing
      const originalToken = impersonation.getOriginalToken();

      // Clear impersonation tokens
      impersonation.clearImpersonation();

      // Try to restore original superadmin session
      const restored = impersonation.restoreOriginalSession();

      return { restored, originalToken };
    },

    onSuccess: ({ restored, originalToken }) => {
      if (restored && originalToken) {
        // Decode original token to restore superadmin user
        const jwtPayload = decodeJwt(originalToken);

        if (jwtPayload) {
          const superadminUser = {
            id: jwtPayload.sub,
            email: jwtPayload.email,
            firstName: 'Super',
            lastName: 'Admin',
            fullName: 'Super Admin',
            role: jwtPayload.roles?.length ? getHighestRole(jwtPayload.roles) : 'SUPER_ADMIN',
            permissions: jwtPayload.permissions || [],
            tenantId: jwtPayload.tenantId,
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          // Update store with superadmin user
          const { login: storeLogin } = useBoundStore.getState();
          storeLogin(superadminUser, undefined);

          toast.success('Impersonation Ended', {
            description: 'You are back as superadmin',
          });

          // Navigate to unified platform tenant directory
          navigate({ to: '/settings/system/tenants' });
          return;
        }
      }

      // Fallback: if restore failed, log out and redirect to login
      const { logout: storeLogout } = useBoundStore.getState();
      storeLogout();

      toast.success('Impersonation Ended', {
        description: 'Please log in again as superadmin',
      });

      navigate({ to: '/login' });
    },
  });
}

// =============================================================================
// ABAC SYNC
// =============================================================================

export function useSyncTenantAbac() {
  return useMutation({
    mutationFn: (tenantId: string) => operationsApi.syncAbacForTenant(tenantId),

    onSuccess: (data) => {
      const total = data.policiesCreated + data.rolesCreated + data.assignmentsCreated;
      toast.success('ABAC Synced', {
        description:
          total === 0
            ? `${data.tenantName} is already up to date`
            : `${data.tenantName}: ${data.policiesCreated} policies, ${data.rolesCreated} roles, ${data.assignmentsCreated} assignments created`,
      });
    },

    onError: (error: unknown) => {
      toast.error('ABAC sync failed', {
        description: getErrorMessage(parseApiError(error)),
      });
    },
  });
}

export function useSyncAllTenantsAbac() {
  return useMutation({
    mutationFn: () => operationsApi.syncAbacForAllTenants(),

    onError: (error: unknown) => {
      toast.error('ABAC sync failed', {
        description: getErrorMessage(parseApiError(error)),
      });
    },
  });
}

export function useExportTenants() {
  return useMutation({
    mutationFn: (filters?: TenantListFilters) => tenantsApi.exportTenants(filters),

    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tenants-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export Complete', {
        description: 'Tenants have been exported to CSV',
      });
    },

    onError: () => {
      toast.error('Export failed', { description: 'Please try again' });
    },
  });
}
